import type { PenciledInputOfferType, IPenciledInputs, Offer } from '@makemydeal/dr-platform-types';
import { BALLOON, FINANCE, LEASE } from '@makemydeal/dr-platform-types';
import { getOfferDetailsByOfferType } from '../selectors/offerNodeSelectors';
import { getAutoTermId, getOfferTermsDetails, getTermsByIdPayment } from '../selectors/offerSelectors';
import { getOfferPenciledInputs } from '../selectors/penciledInputsSelectors';
import type { NonCashOfferType } from '../types/commonTypes';
import { replaceArrayValue } from './common';
// Import pure parsing functions from termIdParser to avoid circular dependencies
import { parseTermId } from './termIdParser';

// Re-export for backward compatibility
export { isExtensionValueValid, joinTermValues, parseTermId } from './termIdParser';

export const validateTermId = (offer: Offer, defaultTermId: string): string => {
    if (!defaultTermId) {
        return defaultTermId;
    }

    const offerTypeParams = parseTermId(defaultTermId);

    if (!offerTypeParams || !offerTypeParams.termIdParams) {
        return defaultTermId;
    }

    const paymentTerm = getTermsByIdPayment(offer, defaultTermId);
    if (!paymentTerm) {
        return defaultTermId;
    }

    const penciledInputs = getOfferPenciledInputs(offer);
    const termIdOptions = {
        offerType: offerTypeParams.offerType,
        paymentTerm: paymentTerm,
        pencilInputs: penciledInputs
    };

    return getAutoTermId(offer, offerTypeParams.termIdParams.term, termIdOptions)!;
};

export const syncDefaultTermIds = (offer: any): void => {
    const offerTypesToLink: NonCashOfferType[] = [LEASE, FINANCE, BALLOON] as const;
    const originalPenciledInputs = getOfferPenciledInputs(offer);
    const originalTermDetails = getOfferTermsDetails(offer);

    // Type-safe mapping from offer type to terms key
    const termsKeyMap = {
        [LEASE]: 'leaseTerms',
        [FINANCE]: 'financeTerms',
        [BALLOON]: 'balloonTerms'
    } as const;

    // Clone penciledInputs to prevent mutation
    const penciledInputs = {
        ...originalPenciledInputs,
        byOfferType: {
            ...originalPenciledInputs.byOfferType,
            [LEASE]: { ...originalPenciledInputs.byOfferType?.[LEASE] },
            [FINANCE]: { ...originalPenciledInputs.byOfferType?.[FINANCE] },
            [BALLOON]: { ...originalPenciledInputs.byOfferType?.[BALLOON] }
        }
    };

    // Clone termDetails
    const termDetails = {
        ...originalTermDetails,
        byId: { ...originalTermDetails.byId },
        allIds: [...(originalTermDetails.allIds || [])],
        leaseTerms: [...(originalTermDetails.leaseTerms || [])],
        financeTerms: [...(originalTermDetails.financeTerms || [])],
        balloonTerms: [...(originalTermDetails.balloonTerms || [])]
    };

    offerTypesToLink.forEach((offerType: NonCashOfferType) => {
        const penciledInput = getOfferDetailsByOfferType(penciledInputs as IPenciledInputs<PenciledInputOfferType>, offerType);
        const defaultTermId = penciledInput.term;
        const termsName = termsKeyMap[offerType];
        const termList = [...termDetails[termsName]];

        termList.forEach((termId: string) => {
            const validatedTermId = validateTermId(offer, termId);

            if (termId !== validatedTermId && termDetails.byId[validatedTermId] == null) {
                if (termId === defaultTermId) {
                    penciledInput.term = validatedTermId;
                }
                termDetails.byId[validatedTermId] = { ...termDetails.byId[termId!], id: validatedTermId };
                delete termDetails.byId[termId];

                termDetails.allIds = replaceArrayValue(termDetails.allIds, termId, validatedTermId);
                termDetails[termsName] = replaceArrayValue(termDetails[termsName], termId, validatedTermId);
            }
        });
    });

    offer.terms = termDetails;
    offer.penciledInputs = penciledInputs;
};
